import { useCallback, useEffect, useRef, useState } from "react";
import { API } from "../../backend";
import "../contractimport.css";

const Icon = {
  File: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  ),
  Check: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  ),
  ChevronDown: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  ),
  ChevronRight: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  ),
  Alert: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-4 h-4">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  ),
  Spinner: () => (
    <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  ),
};

const STEPS = ["Upload", "Extracting", "Review", "Done"];

function StepBar({ current }) {
  return (
    <div className="flex items-center gap-0 mb-8">
      {STEPS.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={label} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border transition-all duration-300 ${done ? "bg-emerald-500 border-emerald-500 text-white" : active ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-400"}`}>
                {done ? <Icon.Check /> : i + 1}
              </div>
              <span className={`mt-1.5 text-xs font-medium tracking-wide ${active ? "text-slate-900" : done ? "text-emerald-600" : "text-slate-400"}`}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 mt-[-14px] transition-colors duration-500 ${done ? "bg-emerald-400" : "bg-slate-200"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function UploadZone({ onFile }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  }, [onFile]);

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      className={`relative cursor-pointer rounded-2xl border-2 border-dashed transition-all duration-200 p-12 text-center ${dragging ? "border-slate-900 bg-slate-50 scale-[1.01]" : "border-slate-200 bg-white hover:border-slate-400 hover:bg-slate-50"}`}
    >
      <input ref={inputRef} type="file" accept=".docx,.pdf" className="hidden" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
      <div className="flex flex-col items-center gap-4">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors ${dragging ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-7 h-7">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12l-3-3m0 0l-3 3m3-3v6m-1.5-15H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
        </div>
        <div>
          <p className="text-base font-semibold text-slate-800">Drop your contract here</p>
          <p className="mt-1 text-sm text-slate-500">or <span className="text-slate-800 underline underline-offset-2">browse files</span></p>
        </div>
        <div className="flex gap-2">
          {[".DOCX", ".PDF"].map((ext) => (
            <span key={ext} className="px-2.5 py-1 rounded-md bg-slate-100 text-slate-500 text-xs font-mono font-medium">{ext}</span>
          ))}
          <span className="px-2.5 py-1 rounded-md bg-slate-100 text-slate-500 text-xs font-medium">max 20 MB</span>
        </div>
      </div>
    </div>
  );
}

function ExtractionProgress({ filename, progress, logs }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 border border-slate-100">
        <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 shrink-0">
          <Icon.File />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">{filename}</p>
          <p className="text-xs text-slate-500">Processing...</p>
        </div>
        <Icon.Spinner />
      </div>
      <div>
        <div className="flex justify-between text-xs text-slate-500 mb-1.5">
          <span>Extracting data</span>
          <span>{progress}%</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-slate-800 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>
      <div className="space-y-1.5 font-mono text-xs max-h-40 overflow-y-auto">
        {logs.map((log, i) => (
          <div key={i} className={`flex items-center gap-2 ${log.ok ? "text-emerald-600" : "text-slate-500"}`}>
            <span className="shrink-0">{log.ok ? "✓" : ">"}</span>
            <span>{log.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EditableField({ label, value, onChange, type = "text" }) {
  const normalizedValue = value ?? "";
  return (
    <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1 font-medium">{label}</p>
      <input
        type={type}
        value={normalizedValue}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Not found"
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-slate-400"
      />
    </div>
  );
}

function updateByPath(source, path, value) {
  if (path.length === 0) {
    return value;
  }

  const [head, ...tail] = path;
  const clone = Array.isArray(source) ? [...source] : { ...(source || {}) };
  clone[head] = updateByPath(clone[head], tail, value);
  return clone;
}

function Section({ title, badge, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-100 rounded-2xl overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-4 bg-white hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-sm text-slate-800">{title}</span>
          {badge !== undefined && <span className="bg-slate-100 text-slate-500 text-xs px-2 py-0.5 rounded-full font-medium">{badge}</span>}
        </div>
        <span className="text-slate-400">{open ? <Icon.ChevronDown /> : <Icon.ChevronRight />}</span>
      </button>
      {open && <div className="px-5 pb-5 bg-white border-t border-slate-100">{children}</div>}
    </div>
  );
}

function ReviewPanel({ data, onChange }) {
  const { parties = [], contract = {}, assets = [], guarantors = [], confidence = {} } = data;

  const updateField = (path, value) => onChange((current) => updateByPath(current, path, value));

  return (
    <div className="space-y-3">
      {confidence.overall && (
        <div className={`flex items-start gap-3 p-3.5 rounded-xl text-sm ${confidence.overall === "high" ? "bg-emerald-50 border border-emerald-100 text-emerald-700" : confidence.overall === "medium" ? "bg-amber-50 border border-amber-100 text-amber-700" : "bg-red-50 border border-red-100 text-red-700"}`}>
          <Icon.Check />
          <div>
            <span className="font-semibold capitalize">{confidence.overall} confidence</span>
            {confidence.notes?.length > 0 && (
              <ul className="mt-1 space-y-0.5 text-xs opacity-80">
                {confidence.notes.map((note, i) => <li key={i}>- {note}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}
      <Section title="Parties" badge={parties.length}>
        <div className="mt-4 space-y-4">
          {parties.map((party, i) => (
            <div key={`${party.role || "party"}-${i}`}>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-2 font-medium">{party.role || `Party ${i + 1}`}</p>
              <div className="grid grid-cols-2 gap-2">
                <EditableField label="Legal name" value={party.legal_name} onChange={(value) => updateField(["parties", i, "legal_name"], value)} />
                <EditableField label="Entity type" value={party.entity_type} onChange={(value) => updateField(["parties", i, "entity_type"], value)} />
                <EditableField label="Contact" value={party.primary_contact} onChange={(value) => updateField(["parties", i, "primary_contact"], value)} />
                <EditableField label="Email" value={party.email} onChange={(value) => updateField(["parties", i, "email"], value)} />
                <EditableField label="Phone" value={party.phone} onChange={(value) => updateField(["parties", i, "phone"], value)} />
                <EditableField label="Street" value={party.address?.street} onChange={(value) => updateField(["parties", i, "address", "street"], value)} />
                <EditableField label="City" value={party.address?.city} onChange={(value) => updateField(["parties", i, "address", "city"], value)} />
                <EditableField label="State" value={party.address?.state} onChange={(value) => updateField(["parties", i, "address", "state"], value)} />
                <EditableField label="ZIP" value={party.address?.zip} onChange={(value) => updateField(["parties", i, "address", "zip"], value)} />
                <EditableField label="Country" value={party.address?.country} onChange={(value) => updateField(["parties", i, "address", "country"], value)} />
              </div>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Contract" defaultOpen>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <EditableField label="Contract No." value={contract.contract_number} onChange={(value) => updateField(["contract", "contract_number"], value)} />
          <EditableField label="Structure" value={contract.structure} onChange={(value) => updateField(["contract", "structure"], value)} />
          <EditableField label="Amount financed" value={contract.amount_financed} onChange={(value) => updateField(["contract", "amount_financed"], value)} />
          <EditableField label="Down payment" value={contract.down_payment} onChange={(value) => updateField(["contract", "down_payment"], value)} />
          <EditableField label="Equipment cost" value={contract.equipment_cost} onChange={(value) => updateField(["contract", "equipment_cost"], value)} />
          <EditableField label="Term (months)" value={contract.term_months} onChange={(value) => updateField(["contract", "term_months"], value)} />
          <EditableField label="Monthly payment" value={contract.payment_amount} onChange={(value) => updateField(["contract", "payment_amount"], value)} />
          <EditableField label="Interest rate" value={contract.interest_rate} onChange={(value) => updateField(["contract", "interest_rate"], value)} />
          <EditableField label="Execution date" value={contract.execution_date} onChange={(value) => updateField(["contract", "execution_date"], value)} />
          <EditableField label="First payment" value={contract.first_payment_date} onChange={(value) => updateField(["contract", "first_payment_date"], value)} />
          <EditableField label="Last payment" value={contract.last_payment_date} onChange={(value) => updateField(["contract", "last_payment_date"], value)} />
          <EditableField label="Governing law" value={contract.governing_law} onChange={(value) => updateField(["contract", "governing_law"], value)} />
          <EditableField label="Late fee %" value={contract.late_fee_percent} onChange={(value) => updateField(["contract", "late_fee_percent"], value)} />
          <EditableField label="Late fee grace days" value={contract.late_fee_grace_days} onChange={(value) => updateField(["contract", "late_fee_grace_days"], value)} />
          <EditableField label="Prepayment penalty" value={contract.prepayment_penalty} onChange={(value) => updateField(["contract", "prepayment_penalty"], value)} />
        </div>
      </Section>
      <Section title="Assets" badge={assets.length}>
        <div className="mt-4 space-y-4">
          {assets.map((asset, i) => (
            <div key={i}>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-2 font-medium">Asset {i + 1}</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2"><EditableField label="Description" value={asset.description} onChange={(value) => updateField(["assets", i, "description"], value)} /></div>
                <EditableField label="Unit #" value={asset.unit_number} onChange={(value) => updateField(["assets", i, "unit_number"], value)} />
                <EditableField label="Serial #" value={asset.serial_number} onChange={(value) => updateField(["assets", i, "serial_number"], value)} />
                <EditableField label="Quantity" value={asset.quantity} onChange={(value) => updateField(["assets", i, "quantity"], value)} />
                <EditableField label="Unit cost" value={asset.unit_cost} onChange={(value) => updateField(["assets", i, "unit_cost"], value)} />
                <EditableField label="Delivery" value={asset.delivery_cost} onChange={(value) => updateField(["assets", i, "delivery_cost"], value)} />
                <EditableField label="Asset class" value={asset.asset_class} onChange={(value) => updateField(["assets", i, "asset_class"], value)} />
              </div>
            </div>
          ))}
        </div>
      </Section>
      {guarantors.length > 0 && (
        <Section title="Guarantors" badge={guarantors.length}>
          <div className="mt-4 space-y-3">
            {guarantors.map((guarantor, i) => (
              <div key={i} className="grid grid-cols-2 gap-2">
                <EditableField label="Name" value={guarantor.name} onChange={(value) => updateField(["guarantors", i, "name"], value)} />
                <EditableField label="Guaranty type" value={guarantor.guaranty_type} onChange={(value) => updateField(["guarantors", i, "guaranty_type"], value)} />
                <EditableField label="Signed date" value={guarantor.signed_date} onChange={(value) => updateField(["guarantors", i, "signed_date"], value)} />
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function SuccessScreen({ data, onReset }) {
  const counts = { Parties: data.parties?.length || 0, Contract: data.contract?.contract_number ? 1 : 0, Assets: data.assets?.length || 0 };
  return (
    <div className="text-center py-4">
      <div className="inline-flex w-16 h-16 rounded-full bg-emerald-100 items-center justify-center mb-5">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-8 h-8 text-emerald-600">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-1">Ready to import</h3>
      <p className="text-sm text-slate-500 mb-8">Your reviewed data has been passed to <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-xs">onImportReady</code></p>
      <div className="grid grid-cols-3 gap-3 mb-8">
        {Object.entries(counts).map(([label, count]) => (
          <div key={label} className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
            <p className="text-2xl font-bold text-slate-900">{count}</p>
            <p className="text-xs text-slate-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>
      <button onClick={onReset} className="text-sm text-slate-500 hover:text-slate-800 underline underline-offset-2 transition-colors">Upload another contract</button>
    </div>
  );
}

// export default function ContractUploader({ apiUrl = "http://localhost:8000/extract", onImportReady, className = "" }) {
export default function ContractUploader({ apiUrl = API + "extract/extract_contract/", onImportReady, className = "" }) {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState([]);
  const [extracted, setExtracted] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const timeoutRef = useRef([]);

  const addLog = (text, ok = false) => setLogs((items) => [...items, { text, ok }]);
  const clearPendingTimeouts = useCallback(() => {
    timeoutRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    timeoutRef.current = [];
  }, []);

  useEffect(() => clearPendingTimeouts, [clearPendingTimeouts]);

  const handleFile = async (selectedFile) => {
    clearPendingTimeouts();
    const ext = selectedFile.name.split(".").pop().toLowerCase();
    if (!["docx", "pdf"].includes(ext)) {
      setError("Only .docx and .pdf files are supported.");
      return;
    }
    setError(null);
    setFile(selectedFile);
    setStep(1);
    setLogs([]);
    setProgress(0);

    [[400, 15, "Parsing document structure..."], [900, 30, "Reading text content..."], [1400, 50, "Sending to extraction engine..."], [2200, 65, "Analysing contract clauses..."], [3000, 80, "Identifying entities..."]].forEach(([ms, pct, msg]) => {
      const timeoutId = setTimeout(() => {
        setProgress(pct);
        addLog(msg);
      }, ms);
      timeoutRef.current.push(timeoutId);
    });

    try {
      const form = new FormData();
      form.append("file", selectedFile);
      const response = await fetch(apiUrl, { method: "POST", body: form });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: "Server error" }));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      const json = await response.json();
      setProgress(100);
      addLog("Extraction complete", true);
      const timeoutId = setTimeout(() => {
        setExtracted(json.data);
        setStep(2);
      }, 600);
      timeoutRef.current.push(timeoutId);
    } catch (err) {
      clearPendingTimeouts();
      setError(err.message);
      setStep(0);
    }
  };

  const handleConfirm = async () => {
    try {
      setSaving(true);
      setError(null);
      await onImportReady?.(extracted);
      setStep(3);
    } catch (err) {
      setError(err?.message || "Unable to import contract.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    clearPendingTimeouts();
    setStep(0);
    setFile(null);
    setExtracted(null);
    setLogs([]);
    setProgress(0);
    setError(null);
    setSaving(false);
  };

  return (
    <div className={`w-full max-w-3xl mx-auto font-sans ${className}`}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Contract Import</h2>
        <p className="text-sm text-slate-500 mt-1">Upload a contract - data is automatically extracted and mapped to your system.</p>
      </div>
      <StepBar current={step} />
      {error && (
        <div className="mb-4 flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">
          <Icon.Alert />
          <span>{error}</span>
        </div>
      )}
      {step === 0 && <UploadZone onFile={handleFile} />}
      {step === 1 && <ExtractionProgress filename={file?.name} progress={progress} logs={logs} />}
      {step === 2 && extracted && (
        <div>
          <ReviewPanel data={extracted} onChange={setExtracted} />
          <div className="flex gap-3 mt-6 justify-end">
            <button onClick={handleReset} className="px-5 py-2.5 rounded-xl text-sm font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors">Cancel</button>
            <button onClick={handleConfirm} disabled={saving} className="px-6 py-2.5 rounded-xl text-sm font-semibold bg-slate-900 text-white hover:bg-slate-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
              {saving ? "Creating..." : "Create Contract ->"}
            </button>
          </div>
        </div>
      )}
      {step === 3 && <SuccessScreen data={extracted} onReset={handleReset} />}
    </div>
  );
}
